/**
 * Guest-visited retail tenants (browser-only) — same family as pendingFavoriteSpContacts.
 * Key `customer:pendingActiveTenants`, max 20, TTL 90d.
 * V2 — server auto-engages on first authenticated tenant touch; browser list retained for guest browse.
 *
 * NOT gated on functional cookie consent — visit staging is product-critical.
 */

import {
  isPlatformChromeTenant,
} from '../../app/platformChromeTenant.js';
import { RESERVED_CUSTOMER_PATH_SEGMENTS } from '../auth/customerAuthPaths.js';

export const PENDING_ACTIVE_TENANTS_STORAGE_KEY = 'customer:pendingActiveTenants';
export const PENDING_ACTIVE_TENANTS_CHANGED_EVENT =
  'customer:pendingActiveTenants-changed';
export const PENDING_ACTIVE_TENANTS_MAX = 20;
export const PENDING_ACTIVE_TENANTS_TTL_MS = 90 * 24 * 60 * 60 * 1000;

export interface PendingActiveTenant {
  tenantCode: string;
  seenAt: string;
}

interface StoredPendingActiveTenant extends PendingActiveTenant {
  /** Epoch ms when staged — used for TTL. */
  storedAt: number;
}

function notifyPendingActiveTenantsChanged(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new Event(PENDING_ACTIVE_TENANTS_CHANGED_EVENT));
}

/** Subscribe to same-tab + cross-tab staged-tenant list changes. */
export function subscribePendingActiveTenants(onChange: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }
  window.addEventListener(PENDING_ACTIVE_TENANTS_CHANGED_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(PENDING_ACTIVE_TENANTS_CHANGED_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

/** Snapshot for `useSyncExternalStore` — raw localStorage string. */
export function getPendingActiveTenantsSnapshot(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.localStorage.getItem(PENDING_ACTIVE_TENANTS_STORAGE_KEY) ?? '';
}

export function getPendingActiveTenantsServerSnapshot(): string {
  return '';
}

function normalizeTenantCode(tenantCode: string): string {
  return tenantCode.trim().toLowerCase();
}

function dedupeKey(tenantCode: string): string {
  return normalizeTenantCode(tenantCode);
}

function isExpired(storedAt: number, nowMs: number): boolean {
  return nowMs - storedAt > PENDING_ACTIVE_TENANTS_TTL_MS;
}

function readRaw(): StoredPendingActiveTenant[] {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(PENDING_ACTIVE_TENANTS_STORAGE_KEY);
    if (raw === null || raw.length === 0) {
      return [];
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((item): item is StoredPendingActiveTenant => {
      if (item === null || typeof item !== 'object') {
        return false;
      }
      const row = item as StoredPendingActiveTenant;
      return (
        typeof row.tenantCode === 'string' &&
        row.tenantCode.trim().length > 0 &&
        typeof row.seenAt === 'string' &&
        typeof row.storedAt === 'number'
      );
    });
  } catch {
    return [];
  }
}

function writeRaw(items: StoredPendingActiveTenant[]): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(
      PENDING_ACTIVE_TENANTS_STORAGE_KEY,
      JSON.stringify(items),
    );
    notifyPendingActiveTenantsChanged();
  } catch {
    /* quota / private mode */
  }
}

function pruneAndDedupe(
  items: StoredPendingActiveTenant[],
  nowMs: number,
): StoredPendingActiveTenant[] {
  const fresh = items.filter((item) => !isExpired(item.storedAt, nowMs));
  const byKey = new Map<string, StoredPendingActiveTenant>();
  for (const item of fresh) {
    const key = dedupeKey(item.tenantCode);
    const existing = byKey.get(key);
    if (existing === undefined || item.storedAt >= existing.storedAt) {
      byKey.set(key, item);
    }
  }
  const deduped = Array.from(byKey.values()).sort((a, b) => b.storedAt - a.storedAt);
  return deduped.slice(0, PENDING_ACTIVE_TENANTS_MAX);
}

function toPublic(item: StoredPendingActiveTenant): PendingActiveTenant {
  return {
    tenantCode: item.tenantCode,
    seenAt: item.seenAt,
  };
}

/** Read staged tenants (TTL-pruned, deduped, capped). */
export function readPendingActiveTenants(
  nowMs: number = Date.now(),
): PendingActiveTenant[] {
  const existing = readRaw();
  const pruned = pruneAndDedupe(existing, nowMs);
  if (pruned.length !== existing.length) {
    writeRaw(pruned);
  }
  return pruned.map(toPublic);
}

/** Tenant codes only (for manual activate-staged body). */
export function readPendingActiveTenantCodes(nowMs: number = Date.now()): string[] {
  return readPendingActiveTenants(nowMs).map((item) => item.tenantCode);
}

/** Whether a tenant code is eligible for guest visit staging. */
export function isStageableRetailTenantCode(tenantCode: string): boolean {
  const trimmed = tenantCode.trim();
  if (trimmed.length === 0) {
    return false;
  }
  if (isPlatformChromeTenant(trimmed)) {
    return false;
  }
  if (RESERVED_CUSTOMER_PATH_SEGMENTS.has(normalizeTenantCode(trimmed))) {
    return false;
  }
  return true;
}

/**
 * Stage a retail tenant while browsing without access token.
 * Dedupes by tenantCode (case-insensitive); newest wins; max 20.
 */
export function stagePendingActiveTenant(input: {
  tenantCode: string;
  seenAt?: string;
  nowMs?: number;
}): void {
  const tenantCode = input.tenantCode.trim();
  if (!isStageableRetailTenantCode(tenantCode)) {
    return;
  }
  const nowMs = input.nowMs ?? Date.now();
  const seenAt = input.seenAt ?? new Date(nowMs).toISOString();
  const next: StoredPendingActiveTenant = {
    tenantCode,
    seenAt,
    storedAt: nowMs,
  };
  writeRaw(pruneAndDedupe([next, ...readRaw()], nowMs));
}

/**
 * Guest retail visit — stage URL tenant when logged out.
 * No-ops when signed in (cookie access token present) or platform/reserved.
 */
export function tryStageGuestActiveTenantVisit(args: {
  tenantCode: string;
  hasAccessToken: boolean;
  nowMs?: number;
}): void {
  if (args.hasAccessToken) {
    return;
  }
  stagePendingActiveTenant({
    tenantCode: args.tenantCode,
    nowMs: args.nowMs,
  });
}

export function clearPendingActiveTenants(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(PENDING_ACTIVE_TENANTS_STORAGE_KEY);
    notifyPendingActiveTenantsChanged();
  } catch {
    /* ignore */
  }
}
